import { useQuery, UseQueryOptions, UseQueryResult, QueryKey, SkipToken, skipToken } from '@tanstack/react-query';
import { makeAuth0BearerAuthFetcher } from '../../../core/requests/auth0-bearer-auth-fetcher';
import { useAuthContext } from '../../auth/context';
import { useLanguage } from '../../../core/i18n/hooks/use-language';

import { retryRequestExceptForClientErrors } from '../services/retry-request-except-for-client-errors';

export type QueryOptions<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = {
  options: Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryFn'>;
  url: string;
  headers?: Record<string, string>;
};

export type QueryParameters<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = QueryOptions<TQueryFnData, TError, TData, TQueryKey> | SkipToken;

export function useAuthenticatedQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(params: QueryParameters<TQueryFnData, TError, TData, TQueryKey>): UseQueryResult<TData, TError> {
  const { retrieveAccessToken } = useAuthContext();
  const currentLanguage = useLanguage();

  return useQuery<TQueryFnData, TError, TData, TQueryKey>(
    params === skipToken
      ? {
          queryFn: skipToken,
          // this typecast is applied because it strictly does not adhere to the TQueryKey type but it is the desired key for this scenario
          queryKey: ['skipToken'] as unknown as TQueryKey,
        }
      : {
          ...params.options,
          retry: params.options.retry ?? retryRequestExceptForClientErrors,
          queryFn: makeAuth0BearerAuthFetcher<TQueryFnData>({
            url: params.url,
            retrieveAccessToken,
            language: currentLanguage,
            headers: params.headers,
          }),
        },
  );
}
